import { DeleteOutlined, LoadingOutlined } from "@ant-design/icons";
import { PauseIcon, PlayIcon } from "@phosphor-icons/react";
import { Flex, Button, Typography, Slider, ConfigProvider } from "antd";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useAntTheme } from "hooks/useAntTheme";

// Only one AudioSample should play at a time across the whole app: when one
// starts, stop and rewind whichever one was previously playing.
let currentlyPlayingAudio: HTMLAudioElement | null = null;

type AudioSampleProps = {
  removeAudioSample?: () => void;
  audio: File | HTMLAudioElement | string; // String is path or base64 data URI
  canDelete?: boolean;
  children?: React.ReactNode;
  containerJustify?: React.CSSProperties["justifyContent"];
  onDemand?: boolean;
  onClickPlay?: () => void;
  loading?: boolean;
  playImmediately?: boolean;
  deleteFunc?: () => void;
  volume?: number;
};

export const AudioSample: React.FC<AudioSampleProps> = ({
  audio,
  removeAudioSample,
  canDelete = true,
  children,
  containerJustify = "space-between",
  onDemand,
  onClickPlay,
  loading,
  playImmediately,
  deleteFunc,
  volume,
}) => {
  const [playing, setPlaying] = useState<boolean>(false);
  const [currentTime, setCurrentTime] = useState<number>(0);
  const animation = useRef(0);
  const { theme } = useAntTheme();
  const [duration, setDuration] = useState<number>(
    Math.floor((audio as HTMLAudioElement)?.duration ?? 0)
  );
  const audioElementRef = useRef<HTMLAudioElement | null>(null);
  const displayTime = useMemo(() => {
    if (!audio) return 0;
    return currentTime;
  }, [currentTime, audio]);

  const playSound = useCallback(async () => {
    const currentAudio = audioElementRef.current;
    if (!currentAudio) return;
    const propsVolume = Math.max(0, Math.min(1, volume ?? 1));
    if (propsVolume !== currentAudio.volume) {
      currentAudio.volume = propsVolume;
    }
    if (currentTime === duration) {
      currentAudio.currentTime = 0;
    }
    if (currentAudio.paused) {
      await currentAudio.play();
    } else {
      currentAudio.pause();
    }
  }, [volume, currentTime, duration]);

  useLayoutEffect(() => {
    const currentAudio = audioElementRef.current;
    if (!currentAudio) return;
    currentAudio.volume = Math.max(0, Math.min(1, volume ?? 1));
  }, [volume]);

  // Create or update audio element when audio prop changes
  const getAudioElement = useCallback(() => {
    if (typeof audio === "string") {
      try {
        return new Audio(URL.createObjectURL(base64toBlob({ data: audio })));
      } catch {
        // Treat as a URL
        return new Audio(audio);
      }
    } else if (audio instanceof File) {
      return new Audio(URL.createObjectURL(audio as Blob));
    } else {
      return audio;
    }
  }, [audio]);

  // Update audio element when audio prop changes
  useEffect(() => {
    audioElementRef.current = getAudioElement();
  }, [getAudioElement]);

  function handleEnded() {
    setPlaying(false);
    cancelAnimationFrame(animation.current);
    setCurrentTime(0);
    if (audioElementRef.current) {
      audioElementRef.current.currentTime = 0;
    }
  }

  useEffect(() => {
    const currentAudio = audioElementRef.current;
    if (!currentAudio || onDemand) return;
    if (playImmediately) {
      playSound().catch((error) =>
        console.error("Error playing sound:", error)
      );
    }

    function updateCurrentTime() {
      if (audioElementRef.current) {
        setCurrentTime(audioElementRef.current.currentTime);
      }
      animation.current = requestAnimationFrame(updateCurrentTime);
    }
    animation.current = requestAnimationFrame(updateCurrentTime);
    // stop animation when component unmounts
    return () => cancelAnimationFrame(animation.current);
  }, [audio, onDemand, playImmediately, volume, playSound]);

  useEffect(() => {
    const currentAudio = audioElementRef.current;
    const isFirefox = navigator.userAgent.toLowerCase().includes("firefox");
    if (!currentAudio) return;

    const assignDuration = () => {
      if (currentAudio) {
        const newDuration = currentAudio.duration;
        if (!Number.isNaN(newDuration)) {
          setDuration(newDuration);
        }
      }
    };

    const onPlay = () => {
      if (currentlyPlayingAudio && currentlyPlayingAudio !== currentAudio) {
        currentlyPlayingAudio.pause();
        currentlyPlayingAudio.currentTime = 0;
      }
      currentlyPlayingAudio = currentAudio;
      setPlaying(true);
    };
    const onPause = () => {
      if (currentlyPlayingAudio === currentAudio) {
        currentlyPlayingAudio = null;
      }
      setPlaying(false);
    };
    currentAudio.addEventListener("play", onPlay);
    currentAudio.addEventListener("pause", onPause);
    currentAudio.addEventListener("loadedmetadata", assignDuration);
    if (isFirefox) {
      currentAudio.addEventListener("canplaythrough", assignDuration);
    }
    currentAudio.addEventListener("ended", handleEnded);

    return () => {
      currentAudio.pause();
      // The "pause" event fires asynchronously, so onPause won't run before the
      // listener below is removed - clear the ref directly here instead.
      if (currentlyPlayingAudio === currentAudio) {
        currentlyPlayingAudio = null;
      }
      currentAudio.removeEventListener("loadedmetadata", assignDuration);
      currentAudio.removeEventListener("canplaythrough", assignDuration);
      currentAudio.removeEventListener("ended", handleEnded);
      currentAudio.removeEventListener("play", onPlay);
      currentAudio.removeEventListener("pause", onPause);
    };
  }, [audio]);

  function handleClick() {
    if (onDemand) {
      onClickPlay?.();
    } else {
      playSound().catch((error) =>
        console.error("Error playing sound:", error)
      );
    }
  }

  const handleSeek = (value: number) => {
    const time = (value / 100) * duration;
    setCurrentTime(time);
    if (audioElementRef.current) {
      audioElementRef.current.currentTime = time;
    }
  };

  const handleSliderChange = (value: number) => {
    handleSeek(value);
  };

  let icon = playing ? (
    <PauseIcon size={20} weight="fill" />
  ) : (
    <PlayIcon size={20} weight="fill" />
  );

  if (loading) {
    icon = (
      <LoadingOutlined
        spin
        style={{
          fontSize: 24,
          opacity: 1,
        }}
      />
    );
  }

  return (
    <Flex
      gap={16}
      align="center"
      justify={containerJustify}
      style={{ width: "100%" }}
    >
      <Flex align="center">
        <Button
          shape="circle"
          style={{ position: "relative", left: "-10px", flex: 0 }}
          onClick={handleClick}
          icon={icon}
          type="text"
        />
        {children}
      </Flex>

      <ConfigProvider
        theme={{
          components: {
            Slider: {
              handleSize: 0,
              railSize: 4,
              handleSizeHover: 0,
              handleLineWidth: 0,
              handleLineWidthHover: 0,
              handleActiveOutlineColor: "transparent",
              trackBg: theme.colorPrimary,
              trackHoverBg: theme.colorPrimary,
              railBg: theme.colorFillSecondary,
              handleColor: theme.colorPrimary,
            },
          },
        }}
      >
        <Slider
          value={onDemand ? 0 : (displayTime / duration) * 100}
          onChange={handleSliderChange}
          disabled={onDemand}
          tooltip={{
            formatter: null,
          }}
          style={{ flex: 1 }}
          min={0}
          max={100}
          step={0.1}
        />
      </ConfigProvider>

      <Flex gap={16} align="center" justify="flex-end">
        {!onDemand && (
          <Typography.Text
            style={{ flex: "1 0", textAlign: "right" }}
            type="secondary"
          >
            {`${Math.floor(displayTime)}s`} / {`${Math.floor(duration)}s`}
          </Typography.Text>
        )}
        {canDelete && (
          <Button
            style={{ flex: "0 1" }}
            type="text"
            icon={<DeleteOutlined />}
            onClick={() => {
              removeAudioSample?.();
              deleteFunc?.();
              audioElementRef.current?.remove();
            }}
          />
        )}
      </Flex>
    </Flex>
  );
};

type Base64 = {
  data: string;
  mime?: string;
};

function base64toBlob({ data, mime = "audio/mp3" }: Base64): Blob {
  const byteString = atob(data);
  const ab = new ArrayBuffer(byteString.length);
  const ia = new Uint8Array(ab);
  for (let i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i);
  }
  return new Blob([ab], { type: mime });
}
